import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Database } from "./database.interface";
import { WebsocketRequest, WebsocketService } from "./websocket";

/* eslint-disable @typescript-eslint/naming-convention */
interface AlulaRequestData {
    http_method: string;
    url_path: string;
    raw_body_content: {
        data?: {
            attributes: {
                [key: string]: number;
            };
        };
    };
}

interface AlulaRequest extends WebsocketRequest {
    ipdatatel_data: AlulaRequestData;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface AlulaResponseData {
    id: string;
    attributes: Record<string, any>;
    type: string;
}

interface AlulaRpcResponseResult {
    data?: AlulaResponseData;
    success?: boolean;
}

interface AlulaRpcResponse {
    error: {
        code: number;
        data: { message: string };
        message: string;
    };
    id: number;
    jsonrpc: string;
    result: AlulaRpcResponseResult;
}

interface AlulaRestResponse {
    errors?: { status: string; code: string; title: string; detail: string };
    data?: AlulaResponseData[] | AlulaResponseData;
    links?: { self: string };
    meta?: {
        page: { number: number; size: number; total: number };
    };
}

@Injectable({
    providedIn: "root",
})
export class AlulaDatabaseService implements Database {
    defaultFilterString = "customOptions[omitRelationships]=true&page[size]=1000";
    requestType = "ipdatatel"; // AKA Alula

    constructor(private websocket: WebsocketService) {}

    private prepareAlulaData(path: string, method = "GET", body = {}): AlulaRequestData {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            http_method: method,
            url_path: path,
            raw_body_content: body,
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    private makeRpcRequest(data: AlulaRequestData): Observable<AlulaRpcResponseResult> {
        return this.makeWebsocketRequest(data, this.requestType).pipe(
            map((response: AlulaRpcResponse) => {
                if (response && !response.error) {
                    return response.result;
                }
                throw response;
            }),
            catchError(err => throwError(err))
        );
    }

    private makeRestRequest(data: AlulaRequestData, requestType = this.requestType): Observable<any> {
        return this.makeWebsocketRequest(data, requestType).pipe(
            map((response: AlulaRestResponse) => {
                if (response && response.errors) {
                    throw new Error(response.errors[0].detail);
                } else if (response) {
                    return response.data;
                } else {
                    return [];
                }
            })
        );
    }

    private makeWebsocketRequest(data: AlulaRequestData, requestType = this.requestType): Observable<AlulaRestResponse | AlulaRpcResponse> {
        /* eslint-disable @typescript-eslint/naming-convention */
        return this.websocket.makeRequest({
            request_type: requestType,
            session_id: "",
            ipdatatel_data: data,
            headers: null,
            metadata: {},
        } as AlulaRequest);
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    public createRecord$(path: string, body: any): Observable<AlulaResponseData> {
        const data = this.prepareAlulaData(`/rest/v1/${path}`, "POST", {
            data: { attributes: body },
        });

        return this.makeRestRequest(data);
    }

    public deleteRecord$(path: string, id: string | number): Observable<AlulaResponseData[]> {
        const data = this.prepareAlulaData(`/rest/v1/${path}/${id}`, "DELETE");

        return this.makeRestRequest(data);
    }

    public getAllRecords$(path: string): Observable<AlulaResponseData[]> {
        return this.getRecords$(path);
    }

    public getRecord$(path: string, id: string | number): Observable<AlulaResponseData> {
        const data = this.prepareAlulaData(`/rest/v1/${path}/${id}?` + this.defaultFilterString);

        return this.makeRestRequest(data);
    }

    public getFilteredRecords$(path: string, filterString: string): Observable<AlulaResponseData[]> {
        const data = this.prepareAlulaData(`/rest/v1/${path}?${filterString}`);

        return this.makeRestRequest(data);
    }

    public getRecords$(path: string, alulaQueryFilters = {}, sorting?: string, rawFiltersString?: string): Observable<AlulaResponseData[]> {
        const filterString = Object.keys(alulaQueryFilters).reduce(
            (_filter, key) =>
                alulaQueryFilters[key] ? `${_filter}&filter[${key}]=${alulaQueryFilters[key]}` : `${_filter}&filter[${key}]`,
            this.defaultFilterString + (rawFiltersString || "") + (sorting ? `&sort=${sorting}` : "")
        );

        const data = this.prepareAlulaData(`/rest/v1/${path}?${filterString}`);

        return this.makeRestRequest(data);
    }

    public updateRecord$(path: string, attributes: any, id: string | number): Observable<AlulaResponseData> {
        const data = this.prepareAlulaData(`/rest/v1/${path}/${id}`, "PATCH", {
            data: { attributes, id },
        });

        return this.makeRestRequest(data);
    }

    private generateId(): number {
        return Math.floor(Math.random() * 10000); // NOSONAR
    }

    public updateRecordRpc$(method: string, params: Record<string, unknown>): Observable<AlulaRpcResponseResult> {
        const data = this.prepareAlulaData("/rpc/v1", "POST", {
            jsonrpc: "2.0",
            method,
            params,
            id: this.generateId(),
        });

        return this.makeRpcRequest(data);
    }

    public createRecordRpc$(method: string, params: Record<string, unknown>): Observable<AlulaRpcResponseResult> {
        return this.updateRecordRpc$(method, params);
    }

    public createAlulaUser$(body: Record<string, unknown>): Observable<any> {
        const data = this.prepareAlulaData("/rest/v1/users", "POST", {
            data: { attributes: body },
        });

        return this.makeRestRequest(data, "create_alula_account");
    }

    public createAlulaDealer$(body: Record<string, unknown>): Observable<any> {
        const data = this.prepareAlulaData("/rest/v1/dealers", "POST", {
            data: { attributes: body },
        });
        return this.makeRestRequest(data, "create_alula_dealer");
    }
}
